import React, { useEffect, useRef, useState, useMemo } from "react";
import { connect } from "react-redux";
import MCard, { MCardControlMethods } from "components/Shared/MCard.tsx";
import { CustomerTypeDropdownEntries } from "typeConstants/customerTypes";
import { DATA_TYPE_DATE, DATA_TYPE_DROPDOWN2 } from "typeConstants/dataTypes";
import { userHasRight } from "selectors/rights";
import { CUSTOMER_SALES_DETAILS_EDIT } from "typeConstants/rights";
import { CustomerSalesDto, EmployeeListDto } from "interfaces/Mueller.Business.Models";
import { getSalesDetails, updateSalesDetails } from "requests/customer";
import { getEmployeesByRoles } from "requests/employee/index";
import {
  BUSINESSDEVELOPMENT_VP,
  BUSINESSDEVELOPMENT_DIRECTOR,
  BUSINESSDEVELOPMENT_MANAGER,
  SALES_INSURANCE_DIRECTOR,
  SALES_INSURANCE_MANAGER,
  SALES_INSURANCE_VICE_PRESIDENT,
  SALES_VALUATION_DIRECTOR,
  SALES_VALUATION_VICE_PRESIDENT
} from "typeConstants/employeeRoles";
import { MSelectOption } from "components/Shared/MSelect";
import * as yup from "yup";

interface CustomerSalesDetailsProps {
  customerNumber: number;
  cellWidth: number;
  // Redux
  canEdit: boolean;
}

const CustomerSalesDetails = (props: CustomerSalesDetailsProps) => {
  const [salesDetails, setSalesDetails] = useState<CustomerSalesDto>();
  const [saleEmployeeOptions, setSaleEmployeeOptions] = useState<MSelectOption[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const mounted = useRef<boolean>(true);
  const cardControlMethods = useRef<MCardControlMethods>();

  useEffect(() => { if (props.customerNumber) getData(); }, [props.customerNumber]);

  const getData = () => {
    getSalesDetails(props.customerNumber, handleSalesDetails, mounted);
    if (!saleEmployeeOptions.length) {
      getEmployeesByRoles([
        BUSINESSDEVELOPMENT_VP,
        BUSINESSDEVELOPMENT_DIRECTOR,
        BUSINESSDEVELOPMENT_MANAGER,
        SALES_INSURANCE_DIRECTOR,
        SALES_INSURANCE_MANAGER,
        SALES_INSURANCE_VICE_PRESIDENT,
        SALES_VALUATION_DIRECTOR,
        SALES_VALUATION_VICE_PRESIDENT
      ],
        handleSalesEmployees,
        mounted);
    }
  };

  const handleSalesDetails = (response: CustomerSalesDto) => {
    setSalesDetails(response);
    if (saleEmployeeOptions.length) {
      setIsLoading(false);
    }
  };

  const handleSalesEmployees = (response: EmployeeListDto[]) => {
    const options = response?.map(e => ({ label: e.firstLastName, value: e.employeeId }));
    // Add Mueller option to allow unassigning sales reps
    options.push({ label: "Mueller", value: null });
    setSaleEmployeeOptions(options);
    if (isLoading) {
      setIsLoading(false);
    }
  };

  const getSalesRepValue = () => {
    const existingOption = saleEmployeeOptions.find(ct => ct.value === salesDetails?.salesRepEmployeeId);
    return existingOption
      ? existingOption
      : { label: salesDetails?.salesRepName, value: -1 };  // Sales rep is inactive user
  };

  const memoizedFields = useMemo(
    () => {
      return [
        {
          key: "salesRepEmployeeId",
          label: "Sales Rep",
          value: getSalesRepValue(),
          editable: props.canEdit,
          dataType: DATA_TYPE_DROPDOWN2,
          dataTypeOptions: { options: saleEmployeeOptions, nullable: false },
        },
        {
          key: "contractEndDate",
          label: "Contract End Date",
          value: salesDetails?.contractEndDate,
          editable: props.canEdit,
          dataType: DATA_TYPE_DATE,
        },
        {
          key: "customerTypeId",
          label: "Customer Type",
          value: CustomerTypeDropdownEntries.find(ct => ct.value === salesDetails?.customerTypeId),
          editable: props.canEdit,
          validation: yup.string().required("Customer Type is required"),
          dataType: DATA_TYPE_DROPDOWN2,
          dataTypeOptions: { options: CustomerTypeDropdownEntries, nullable: false },
        },
        {
          key: "customerOnboardingDate",
          label: "Onboarding Date",
          value: salesDetails?.customerOnboardingDate,
          editable: props.canEdit,
          dataType: DATA_TYPE_DATE,
        },
        {
          key: "customerFirstOrderDate",
          label: "First Order Date",
          value: salesDetails?.customerFirstOrderDate,
          editable: props.canEdit,
          dataType: DATA_TYPE_DATE,
        },               
      ];
    },
    [salesDetails, saleEmployeeOptions]
  );

  const onEditFinished = (fieldsChanged) => {
    if (fieldsChanged.length) {
      setIsLoading(true);
      let updated = { ...salesDetails, ...cardControlMethods.current.getEditedDataObject(true) };
      // If you delete the value in the dropdown, then click save, we end up with an empty array as the value
      if (Array.isArray(updated.salesRepEmployeeId)) {
        updated.salesRepEmployeeId = null;
      }
      updateSalesDetails(props.customerNumber, updated, getData, mounted);
    }
  };

  return (
    <MCard
      isFetching={isLoading || !props.customerNumber}
      title="Sales Details"
      cellWidth={props.cellWidth}
      fields={memoizedFields}
      isEditable={props.canEdit}
      onEditFinished={onEditFinished}
      getControlMethods={cm => cardControlMethods.current = cm}
    />
  );
};

const mapStateToProps = state => ({
  canEdit: userHasRight(CUSTOMER_SALES_DETAILS_EDIT)(state),
});

export default connect(mapStateToProps, null)(CustomerSalesDetails);